import { useState } from 'react'
import { getLocale, mt, type MarketKey } from '../lib/i18n'
import { announceSaved } from '../lib/saved'
import { applyFramesPref, framesCalm } from '../lib/framesPref'
import { applyLook, choiceOf, choiceToPatch, saveLook, type LookChoice, type LookState } from '../lib/look'

/** The seven looks on offer. Used at sign-up (a preview) and on the Account page (saved at once). */
export const CHOICES: { id: LookChoice; label: MarketKey }[] = [
  { id: 'island', label: 'lookIsland' },
  { id: 'shore', label: 'lookShore' },
  { id: 'bubbles', label: 'lookBubbles' },
  { id: 'town', label: 'lookTown' },
  { id: 'sunset', label: 'lookSunset' },
  { id: 'palms', label: 'lookPalms' },
  { id: 'halloween', label: 'lookHalloween' },
]

export function LookChoices({ value, onPick, label }: { value: LookChoice; onPick: (next: LookChoice) => void; label: string }) {
  return <div role="radiogroup" aria-label={label} className="look-choices">
    {CHOICES.map(c => <button key={c.id} type="button" role="radio" aria-checked={value === c.id} className={value === c.id ? 'on' : ''} onClick={() => onPick(c.id)}>
      <span className={`look-swatch look-swatch-${c.id}`} aria-hidden="true"/>{mt(c.label)}
    </button>)}
  </div>
}

/** The Account page version: each tap saves to the account and applies at once, so it can be judged by looking. */
export default function LookPicker({ state, onChange }: { state: LookState; onChange: (next: LookState) => void }) {
  const [failed, setFailed] = useState(false), [calm, setCalm] = useState(framesCalm)
  const save = (patch: Parameters<typeof saveLook>[0]) => {
    setFailed(false)
    void saveLook(patch).then(next => { onChange(next); applyLook(next); announceSaved() }).catch(() => setFailed(true))
  }
  return <div className="look-picker">
    <LookChoices value={choiceOf(state)} label={mt('acctLook')} onPick={c => save(choiceToPatch(c))}/>
    <label className="acct-switch look-sound">
      <input type="checkbox" role="switch" checked={state.sound} disabled={state.look === 'island'} onChange={e => save({ sound: e.target.checked })}/>
      <span>{mt('lookSound')}<small>{mt('lookSoundSub')}</small></span>
    </label>
    <label className="acct-switch look-sound">
      <input type="checkbox" role="switch" checked={!calm} onChange={e => { const c = !e.target.checked; setCalm(c); applyFramesPref(c); announceSaved() }}/>
      <span>{getLocale() === 'en' ? 'Show frames and animation' : 'Mostrar marcos y animación'}<small>{getLocale() === 'en' ? 'Off turns every frame into a plain colored border and stops all movement on this device.' : 'Apagado, cada marco es un borde de color liso y nada se mueve en este dispositivo.'}</small></span>
    </label>
    <small className="look-autosave">{getLocale() === 'en' ? 'Changes save as soon as you tap.' : 'Los cambios se guardan al tocar.'}</small>
    {failed && <p role="alert" className="look-failed">{mt('error')}</p>}
  </div>
}
